/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { App, Tooltip } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightSemiBold,
  weightRegular,
  paragraphXSmall,
} from 'src/components/css'

interface Props {
  playerId: string
  /** Chip label (uppercased). Defaults to "PLAYER ID". */
  label?: string
  /**
   * Human-readable name of the id, used for the copy button's tooltip
   * ("Copy <name>") and its aria-label. Separate from `label` because that one
   * is ALL CAPS for the chip's styling and `capitalizeWords` can't down-case it.
   * Defaults to "Player ID".
   */
  idName?: string
}

/**
 * "<LABEL> <id>" chip with a copy button, shown at the right of the Personal
 * Information section title (Figma 20023:1967). Copies the id to the clipboard.
 * Defaults to the player id; pass `label` + `idName` to reuse it for other ids
 * (e.g. "SALESFORCE ID" / "Salesforce ID" on Canlan).
 */
const PlayerIdChip: React.FC<Props> = ({
  playerId,
  label = 'PLAYER ID',
  idName = 'Player ID',
}) => {
  const { message } = App.useApp()
  const copyTitle = capitalizeWords(`Copy ${idName}`)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(playerId)
      message.success(`${idName} copied to clipboard`)
    } catch {
      message.error(`Couldn't copy ${idName}`)
    }
  }

  return (
    <div css={chipStyle}>
      <span css={labelStyle}>{label}</span>
      <span css={valueStyle}>{playerId}</span>
      <Tooltip title={copyTitle}>
        <button
          type='button'
          css={copyButtonStyle}
          onClick={copy}
          aria-label={copyTitle}
        >
          <CopyOutlined css={copyIconStyle} />
        </button>
      </Tooltip>
    </div>
  )
}

export default PlayerIdChip

// ─── Styles ────────────────────────────────────────────────────────────────

const chipStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${colors.SECONDARY_200};
`

const labelStyle = css`
  ${paragraphXSmall}
  ${weightSemiBold}
  ${font.body}
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 1.6px;
`

const valueStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  ${font.body}
  color: ${colors.NEUTRAL_50};
`

const copyButtonStyle = css`
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 0;
`

const copyIconStyle = css`
  font-size: 16px;
  color: ${colors.PRIMARY};
`
